import { useNavigate } from "react-router-dom";
import { Icons } from "../lib/icons";
import { Btn, Pill, PersonaPill, ResultPill, FactorChip } from "../lib/primitives";
import {
  Sparkline,
  AreaChart,
  Donut,
  StackBar,
  ORANGE,
} from "../lib/charts";
import { fmt } from "../lib/format";
import { useAnalytics, useAuditLog, useUserStats } from "../lib/hooks";
import { GetStarted } from "./Quickstart";

const SCREEN_TO_PATH: Record<string, string> = {
  dashboard: "/",
  quickstart: "/quickstart",
  logs: "/logs",
  users: "/users",
  keys: "/keys",
  policy: "/policy",
  branding: "/sign-in-page",
  hosted: "/sign-in-page",
  team: "/team",
  billing: "/billing",
  settings: "/settings",
};

function Kpi({
  label,
  value,
  unit,
  delta,
  sub,
  spark,
  inverted,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  delta?: number;
  sub?: string;
  spark?: number[];
  inverted?: boolean;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="ad-kpi">
        <div className="ad-kpi-label">{label}</div>
        <div className="ad-kpi-value">
          <span className="ad-skel" style={{ width: 96, height: 28 }}>0</span>
        </div>
        <div className="ad-kpi-delta">
          <span className="ad-skel" style={{ width: 64, height: 12 }}>0</span>
        </div>
        {spark !== undefined && (
          <div className="ad-kpi-spark">
            <span className="ad-skel" style={{ width: "100%", height: 28 }}>&nbsp;</span>
          </div>
        )}
      </div>
    );
  }
  const up = inverted ? (delta ?? 0) < 0 : (delta ?? 0) > 0;
  const direction = !delta ? "flat" : up ? "up" : "down";
  const pct = delta === undefined ? "" : `${delta >= 0 ? "+" : ""}${(delta * 100).toFixed(1)}%`;
  const ArrowIcon = up ? Icons.TrendUp : Icons.TrendDown;
  return (
    <div className="ad-kpi">
      <div className="ad-kpi-label">{label}</div>
      <div className="ad-kpi-value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      <div className={`ad-kpi-delta ${direction === "up" ? "up" : direction === "down" ? "down" : ""}`}>
        {direction !== "flat" && <ArrowIcon size={12} />}
        {pct && <span>{pct}</span>}
        <span style={{ color: "var(--ad-fg-3)", marginLeft: pct ? 4 : 0 }}>{sub}</span>
      </div>
      {spark && (
        <div className="ad-kpi-spark">
          <Sparkline data={spark} height={28} />
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  sub,
  pill,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  pill?: React.ReactNode;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 12, color: "var(--ad-fg-2)" }}>{label}</span>
      <div style={{ textAlign: "right" }}>
        <div className="ad-num" style={{ fontFamily: "var(--rb-font-mono)", fontSize: 12 }}>{value}</div>
        <div style={{ fontSize: 10, color: "var(--ad-fg-3)" }}>{sub}</div>
      </div>
      {pill}
    </div>
  );
}

function Legend({ color, label, muted }: { color: string; label: React.ReactNode; muted?: boolean }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: muted ? "var(--ad-fg-3)" : "var(--ad-fg-2)" }}>
      <span style={{ width: 10, height: 2, background: color, borderRadius: 2 }} />
      {label}
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const onNav = (id: string) => navigate(SCREEN_TO_PATH[id] ?? "/");

  const { data: analytics, isLoading: kpiLoading } = useAnalytics();
  const { data: userStats } = useUserStats();
  // Real KPIs only. While the fetch is pending, the Kpi component renders
  // a shimmer; we never show mock numbers in place of unknown reality.
  // Values are lifetime totals; deltas are the trailing window vs the window
  // before it (server-computed), so the arrow reflects real momentum.
  const k = analytics
    ? {
        authVolume: {
          value: analytics.totalVerifications,
          delta: analytics.deltas.authVolume,
        },
        successRate: {
          value:
            analytics.totalVerifications > 0
              ? +(
                  (100 * analytics.successCount) /
                  analytics.totalVerifications
                ).toFixed(1)
              : 0,
          delta: analytics.deltas.successRate,
        },
        enrolledUsers: {
          value: analytics.totalEnrollments,
          delta: analytics.deltas.enrolledUsers,
        },
      }
    : null;
  // Sparklines have no backend yet — leave them undefined so the Kpi
  // doesn't render a sparkline row at all (was previously synthesised
  // from the mock auth-volume series, which read as live).

  // Recent activity: the latest verification events from the audit log,
  // adapted (metadata-only) to the table's row shape.
  const { data: recentAudit } = useAuditLog({ limit: 50 });
  const recentRows = (recentAudit ?? [])
    .filter(
      (e) =>
        e.action === "verification.success" ||
        e.action === "verification.failed",
    )
    .slice(0, 6)
    .map((e) => {
      const meta = (e.metadata ?? {}) as Record<string, unknown>;
      return {
        id: e.id,
        tsLabel: new Date(e.createdAt).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }),
        userExtId: (meta.externalUserId as string) ?? e.externalUserId ?? e.providerSubject ?? "–",
        persona: (meta.personaType as string) === "agent" ? "agent" : "human",
        factors: Array.isArray(meta.factors) ? (meta.factors as string[]) : [],
        result: e.action === "verification.success" ? "success" : "fail-factor",
      };
    });

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <h1 className="ad-h1">Overview</h1>
          <div className="ad-sub">
            How sign-ins are going. You see counts and outcomes only: nobody on your team,
            or at AuthenSee, can see what people use to sign in.
          </div>
        </div>
        <div className="ad-page-head-r">
          <Btn kind="primary" leading={<Icons.Plus size={14} />} onClick={() => onNav("keys")}>New API key</Btn>
        </div>
      </div>

      <GetStarted hideWhenDone />
      <div style={{ height: 18 }} />

      {/* KPI row — real fetch, shimmer while pending */}
      <div className="ad-grid-kpi">
        <Kpi label="Sign-ins" value={k ? fmt(k.authVolume.value) : ""} delta={k?.authVolume.delta ?? 0} sub="vs previous 30 days" loading={kpiLoading || !k} />
        <Kpi label="Success rate" value={k ? `${k.successRate.value}` : ""} unit="%" delta={k?.successRate.delta ?? 0} sub="vs previous 30 days" loading={kpiLoading || !k} />
        <Kpi label="People set up" value={k ? fmt(k.enrolledUsers.value) : ""} delta={k?.enrolledUsers.delta ?? 0} sub="vs previous 30 days" loading={kpiLoading || !k} />
        <Kpi label="Active this week" value={userStats ? fmt(userStats.active7d) : ""} sub="signed in, last 7 days" loading={kpiLoading || !userStats} />
        <Kpi label="Liveness pass rate" value={analytics?.liveness.trials ? `${Math.round(100 * analytics.liveness.passed / analytics.liveness.trials)}` : "—"} unit={analytics?.liveness.trials ? "%" : undefined} sub={analytics?.liveness.trials ? `${analytics.liveness.trials} checks, last 30 days` : "No checks in the last 30 days"} loading={kpiLoading || !analytics} />
      </div>

      <div style={{ height: 18 }} />

      {/* Auth-volume area chart — real, from analytics.series (#30). */}
      <div className="ad-card">
        <div className="ad-card-head">
          <div>
            <div className="ad-card-title">Sign-ins</div>
            <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>Last 30 days</div>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Legend color={ORANGE} label="Total" />
            <Legend color="var(--ad-fg-4)" label="Failed" muted />
          </div>
        </div>
        {analytics ? (
          <AreaChart data={analytics.series} height={260} valueKey="total" labelKey="date" />
        ) : (
          <div className="ad-skel" style={{ width: "100%", height: 260 }}>&nbsp;</div>
        )}
      </div>

      <div style={{ height: 18 }} />

      {/* Outcome split + persona mix — two compact ratio cards, real. */}
      <div className="ad-grid-2">
        {/* Outcome split — real, from analytics success/failure counts. */}
        <div className="ad-card">
          <div className="ad-card-head">
            <div className="ad-card-title">Outcomes</div>
            <div style={{ fontSize: 14, color: "var(--ad-fg-3)" }}>All time</div>
          </div>
          {analytics ? (
            (() => {
              const total = analytics.totalVerifications;
              const okPct =
                total > 0 ? Math.round((1000 * analytics.successCount) / total) / 10 : 0;
              const failPct =
                total > 0 ? Math.round((1000 * analytics.failureCount) / total) / 10 : 0;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                  <Donut value={analytics.successCount} total={total || 1} label={`${okPct}%`} sub="signed in" />
                  <div className="ad-stack-md" style={{ flex: 1 }}>
                    <Row label="Signed in" value={fmt(analytics.successCount)} sub={`${okPct}%`} pill={<Pill kind="success" dot>OK</Pill>} />
                    <Row label="Failed" value={fmt(analytics.failureCount)} sub={`${failPct}%`} pill={<Pill kind="error" dot>Failed</Pill>} />
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="ad-skel" style={{ width: "100%", height: 120 }}>&nbsp;</div>
          )}
        </div>

        {/* People vs AI agents */}
        <div className="ad-card">
          <div className="ad-card-head">
            <div className="ad-card-title">People and AI agents</div>
            <div style={{ fontSize: 14, color: "var(--ad-fg-3)" }}>Who signs in</div>
          </div>
          {analytics ? (
            (() => {
              const human = analytics.personaMix.human;
              const agent = analytics.personaMix.agent;
              const totalP = human + agent;
              const humanFrac = totalP > 0 ? human / totalP : 0;
              const agentFrac = totalP > 0 ? agent / totalP : 0;
              return (
                <div className="ad-stack-md">
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 36, fontWeight: 500, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: "var(--ad-fg-1)" }}>
                      {(humanFrac * 100).toFixed(1)}%
                    </span>
                    <div className="ad-stack-sm">
                      <Pill kind="orange">People</Pill>
                      <span style={{ fontSize: 13, color: "var(--ad-fg-3)" }}>{fmt(human)} sign-ins</span>
                    </div>
                  </div>
                  <StackBar items={[
                    { label: "People", value: humanFrac, color: ORANGE, opacity: 1 },
                    { label: "AI agents", value: agentFrac, color: ORANGE, opacity: 0.30 },
                  ]} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--ad-fg-3)" }}>
                    <span>AI agents: {(agentFrac * 100).toFixed(1)}% ({fmt(agent)})</span>
                    <a className="ad-card-action" onClick={() => onNav("logs")}>See activity →</a>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="ad-skel" style={{ width: "100%", height: 120 }}>&nbsp;</div>
          )}
        </div>
      </div>

      <div style={{ height: 18 }} />

      {/* Recent activity — real, from the audit log (verification events). */}
      <div className="ad-card">
        <div className="ad-card-head">
          <div className="ad-card-title">Recent activity</div>
          <a className="ad-card-action" onClick={() => onNav("logs")}>See all <Icons.ArrowR size={12} /></a>
        </div>
        <table className="ad-table">
          <thead>
            <tr>
              <th style={{ width: 90 }}>Time</th>
              <th>External user ID</th>
              <th>Who</th>
              <th>Method</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {recentRows.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ fontSize: 14 }}>No sign-ins yet.</td>
              </tr>
            )}
            {recentRows.map((l) => (
              <tr key={l.id} className="is-selectable">
                <td className="mono">{l.tsLabel}</td>
                <td className="mono">{l.userExtId}</td>
                <td><PersonaPill persona={l.persona} /></td>
                <td><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{l.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div></td>
                <td><ResultPill result={l.result} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
